/**
 * Token Name/Symbol Blacklist
 * Add offensive, inappropriate, or problematic terms here
 */

// List of blacklisted words/phrases (case-insensitive)
const BLACKLISTED_TERMS = [
  // Add your blacklisted terms here
  // Example categories:
  
  // Racial slurs - all ethnicities
  'nigger', 'nigga', 'n1gger', 'n1gga', 'nig', 'negro',
  'wetback', 'beaner', 'spic', 'sp1c', 'mojado',
  'chink', 'ch1nk', 'gook', 'slant', 'zipperhead',
  'cracker', 'honky', 'whitey', 'redneck',
  'kike', 'k1ke', 'heeb',
  'towelhead', 'sandnigger', 'terrorist',
  
  // Hate speech
  'hitler', 'nazi', 'swastika', 'holocaust',
  'killjews', 'fuckjews', 'hatejews',
  'kkk', 'whitesupremacy', 'whitepower',
  
  // Offensive terms
  'fuck', 'shit', 'bitch', 'cunt', 'dick', 'pussy', 'ass',
  'fck', 'fuk', 'sh1t', 'b1tch', 'fvck', 'f*ck',
  'motherfucker', 'mfer', 'asshole', 'bastard',
  
  // Scam-related
  'scam', 'rug', 'rugpull', 'rugger', 'ponzi',
  
  // Sexual content
  'porn', 'sex', 'xxx',
  
  // Add more as needed...
];

/**
 * Check if a string contains any blacklisted terms
 * @param text Text to check (token name or symbol)
 * @returns Object with isBlacklisted flag and matched term
 */
export function checkBlacklist(text: string): { isBlacklisted: boolean; matchedTerm?: string } {
  if (!text) return { isBlacklisted: false };
  
  const normalized = text.toLowerCase().trim();
  
  for (const term of BLACKLISTED_TERMS) {
    if (normalized.includes(term.toLowerCase())) {
      return { isBlacklisted: true, matchedTerm: term };
    }
  }
  
  return { isBlacklisted: false };
}

/**
 * Validate token name and symbol against blacklist
 * @param name Token name
 * @param symbol Token symbol
 * @returns Validation result with error message if blacklisted
 */
export function validateTokenContent(
  name: string, 
  symbol: string,
  description?: string,
  website?: string,
  twitter?: string,
  telegram?: string,
  discord?: string
): { valid: boolean; error?: string } {
  const nameCheck = checkBlacklist(name);
  if (nameCheck.isBlacklisted) {
    return {
      valid: false,
      error: `Token name contains inappropriate content. Please choose a different name.`
    };
  }
  
  const symbolCheck = checkBlacklist(symbol);
  if (symbolCheck.isBlacklisted) {
    return {
      valid: false,
      error: `Token symbol contains inappropriate content. Please choose a different symbol.`
    };
  }

  if (description) {
    const descCheck = checkBlacklist(description);
    if (descCheck.isBlacklisted) {
      return {
        valid: false,
        error: `Token description contains inappropriate content. Please revise your description.`
      };
    }
  }

  if (website) {
    const websiteCheck = checkBlacklist(website);
    if (websiteCheck.isBlacklisted) {
      return {
        valid: false,
        error: `Website URL contains inappropriate content. Please use a different URL.`
      };
    }
  }

  if (twitter) {
    const twitterCheck = checkBlacklist(twitter);
    if (twitterCheck.isBlacklisted) {
      return {
        valid: false,
        error: `Twitter handle contains inappropriate content. Please use a different handle.`
      };
    }
  }

  if (telegram) {
    const telegramCheck = checkBlacklist(telegram);
    if (telegramCheck.isBlacklisted) {
      return {
        valid: false,
        error: `Telegram link contains inappropriate content. Please use a different link.`
      };
    }
  }

  if (discord) {
    const discordCheck = checkBlacklist(discord);
    if (discordCheck.isBlacklisted) {
      return {
        valid: false,
        error: `Discord link contains inappropriate content. Please use a different link.`
      };
    }
  }
  
  return { valid: true };
}

